import { useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, Spin, Typography, message } from "antd";
import dayjs from "dayjs";

import {
  ChallengeParticipantSource,
  ChallengeParticipantStatus,
  type ChallengeTaskConfig,
  ChallengeTaskType,
  getChallengeRecordingEventDates,
} from "@fitness-app/data-models/entities/Challenge";
import { HabitStatus } from "@fitness-app/data-models/entities/Habit";

import { TraineeTargetingPanel, useTraineeTargeting } from "~/components/TraineeTargeting";
import { emptyTraineeTargetSpec, type TraineeTargetSpec } from "~/components/TraineeTargeting/types";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";

import { scheduleChallengeRecordingWorkouts } from "./scheduleChallengeRecordingWorkouts";

type Props = {
  open: boolean;
  challengeId: string;
  onClose: () => void;
};

type ParticipantRow = {
  traineeId: string;
  status: string;
};

type TaskRow = {
  type: string;
  habitTemplateId: string | null;
  config: ChallengeTaskConfig | null;
};

export const ChallengeCohortAssignModal: FunctionComponent<Props> = ({ open, challengeId, onClose }) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const [spec, setSpec] = useState<TraineeTargetSpec>(emptyTraineeTargetSpec);

  const participantsQuery = useQuery({
    queryKey: ["challenge-cohort-participants", challengeId],
    enabled: open && Boolean(challengeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_participant")
        .select("traineeId, status")
        .eq("challengeId", challengeId)
        .returns<ParticipantRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  // Habit + recording tasks: both need per-trainee provisioning when the cohort grows.
  const tasksQuery = useQuery({
    queryKey: ["challenge-provisionable-tasks", challengeId],
    enabled: open && Boolean(challengeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_task")
        .select("type, habitTemplateId, config")
        .eq("challengeId", challengeId)
        .in("type", [ChallengeTaskType.Habit, ChallengeTaskType.ExerciseRecording])
        .returns<TaskRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  // Trainees currently counted as part of the cohort (active/pending) are pre-selected.
  const activeParticipantIds = useMemo(
    () =>
      (participantsQuery.data ?? [])
        .filter((participant) => participant.status !== ChallengeParticipantStatus.Left)
        .map((participant) => participant.traineeId),
    [participantsQuery.data],
  );

  // Seed the selection once per modal open — background refetches must not clobber in-progress edits.
  const seededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && participantsQuery.data && !seededRef.current) {
      seededRef.current = true;
      setSpec({ ...emptyTraineeTargetSpec, manualIds: activeParticipantIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, participantsQuery.data]);

  const { resolved, trainees, isLoading: targetingLoading } = useTraineeTargeting(spec, open);
  const traineeById = useMemo(() => new Map(trainees.map((trainee) => [trainee.id, trainee])), [trainees]);

  const assignMutation = useMutation({
    mutationFn: async (selectedTraineeIds: string[]) => {
      const initiallyAssigned = activeParticipantIds;
      const toAssign = selectedTraineeIds.filter((id) => !initiallyAssigned.includes(id));
      const toUnassign = initiallyAssigned.filter((id) => !selectedTraineeIds.includes(id));
      const nowIso = dayjs().toISOString();

      // Fresh read (not the modal-open snapshot): another session may have assigned some of these
      // trainees in the meantime — their recording workouts are already scheduled, and re-sending
      // the batch would duplicate them (each batch mints new workout ids, so no conflict key saves us).
      const { data: freshParticipants, error: freshParticipantsError } = await supabase
        .from("challenge_participant")
        .select("traineeId")
        .eq("challengeId", challengeId)
        .returns<Pick<ParticipantRow, "traineeId">[]>();

      if (freshParticipantsError) {
        throw new Error(freshParticipantsError.message);
      }

      const existingParticipantIds = new Set((freshParticipants ?? []).map((row) => row.traineeId));

      if (toAssign.length > 0) {
        const rows = toAssign.map((traineeId) => ({
          challengeId,
          traineeId,
          userId: traineeById.get(traineeId)?.userId ?? null,
          trainerId: null,
          joinedAt: nowIso,
          source: ChallengeParticipantSource.Assigned,
          status: ChallengeParticipantStatus.Active,
        }));

        // No ignoreDuplicates: a trainee who previously left must be flipped back to Active on conflict.
        const { error } = await supabase
          .from("challenge_participant")
          .upsert(rows as never, { onConflict: "challengeId,traineeId" });

        if (error) {
          throw new Error(error.message);
        }
      }

      if (toUnassign.length > 0) {
        const { error } = await supabase
          .from("challenge_participant")
          .update({ status: ChallengeParticipantStatus.Left } as never)
          .eq("challengeId", challengeId)
          .in("traineeId", toUnassign);

        if (error) {
          throw new Error(error.message);
        }
      }

      // Provision habit instances for newly-assigned trainees for every habit task on this challenge.
      const tasks = tasksQuery.data ?? [];
      const habitTemplateIds = tasks
        .filter((task) => task.type === ChallengeTaskType.Habit)
        .map((task) => task.habitTemplateId)
        .filter((id): id is string => Boolean(id));

      if (toAssign.length > 0 && habitTemplateIds.length > 0) {
        const today = dayjs().format("YYYY-MM-DD");
        const habitRows = habitTemplateIds.flatMap((templateId) =>
          toAssign.map((traineeId) => ({
            templateId,
            traineeId,
            userId: traineeById.get(traineeId)?.userId ?? null,
            createdBy: memberId ?? null,
            status: HabitStatus.Active,
            startDate: today,
          })),
        );

        const { error } = await supabase
          .from("client_habit")
          .upsert(habitRows as never, { onConflict: "templateId,traineeId", ignoreDuplicates: true });

        if (error) {
          throw new Error(error.message);
        }
      }

      // Re-apply each recording task's backing workout for genuinely new participants (trainees with
      // no prior participant row — re-assigning someone who left must not duplicate their workouts).
      // The server fans out to future dates only: past ones are outside their window.
      const newTraineeIds = toAssign.filter((id) => !existingParticipantIds.has(id));
      const recordingTasks = tasks.filter(
        (task) => task.type === ChallengeTaskType.ExerciseRecording && task.config?.workoutTemplate,
      );

      const today = dayjs().startOf("day");
      const hasFutureRecordingDates = recordingTasks.some((task) =>
        getChallengeRecordingEventDates(task.config).some((date) => !dayjs(date).isBefore(today, "day")),
      );

      if (newTraineeIds.length > 0 && hasFutureRecordingDates) {
        try {
          await scheduleChallengeRecordingWorkouts({ challengeId, traineeIds: newTraineeIds });
          void message.info(`Zaplanowano treningi z nagraniem dla ${newTraineeIds.length} uczestniczek`);
        } catch (batchError) {
          void message.warning(
            `Przypisania zapisane, ale nie udało się zaplanować treningów z nagraniem: ${
              batchError instanceof Error ? batchError.message : "nieznany błąd"
            }`,
          );
        }
      }
    },
    onSuccess: () => {
      void message.success("Przypisania zaktualizowane");
      void queryClient.invalidateQueries({ queryKey: ["challenge-participants", challengeId] });
      void queryClient.invalidateQueries({ queryKey: ["challenge-leaderboard", challengeId] });
      void queryClient.invalidateQueries({ queryKey: ["challenge-cohort-participants", challengeId] });
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać przypisań: ${error.message}`);
    },
  });

  const isLoading = participantsQuery.isLoading || tasksQuery.isLoading || targetingLoading;

  return (
    <Modal
      title={<Typography.Text>Przypisz klientki do wyzwania</Typography.Text>}
      open={open}
      onCancel={onClose}
      onOk={() => assignMutation.mutate(resolved.map((trainee) => trainee.id))}
      okText="Zapisz przypisania"
      cancelText="Anuluj"
      confirmLoading={assignMutation.isLoading}
      width={680}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <TraineeTargetingPanel value={spec} onChange={setSpec} />
      )}
      <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
        Punktacja przeliczana jest automatycznie co 30 minut – nie musisz nic dodatkowo uruchamiać.
      </Typography.Paragraph>
    </Modal>
  );
};

export default ChallengeCohortAssignModal;
